import { faFileLines } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedTime } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { menusSlice } from '»components/Menus';
import { useCurrentPage } from '»utils/customHooks';
import { TrlTimeFormatted } from '»utils/date';
import { TrlNumberFormatted } from '»utils/number';

import { TrlNbsp } from '../TrlNbsp';
import {
  DataLabel,
  DataValue,
  DataWrapper,
  Separator,
  WrapperValues,
} from './TrlLabelValue.styles';

const EmptyField = (isDetail) => {
  return <DataValue isDetail={isDetail}>-</DataValue>;
};

export const TrlLabelValue = ({
  label,
  values,
  isDetail,
  isDocument,
  documents,
  isWallet,
  type,
  decimalDigits,
  startDecorator,
  endDecorator,
  ...rest
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isFundsListPage } = useCurrentPage();

  const openFilesPreview = (event, noteAttachId, fileName) => {
    event.stopPropagation();

    navigate(`/Market/${isFundsListPage ? 'Funds' : 'Primary'}/ProofPreview`, {
      state: { fileId: noteAttachId, fileName },
    });

    dispatch(
      menusSlice.actions.setActiveMenuItem({
        activeMenuItem: `MARKET:${isFundsListPage ? 'FUNDS' : 'PRIMARY'}:PROOF`,
      }),
    );
  };
  const theme = useTheme();
  const truncate = (str, length) => {
    if (str?.length > length) return `${str.substring(0, length)}...`;

    return str;
  };
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  const getValue = () => {
    if (isDocument) {
      return documents ? (
        documents.map((document) => {
          return (
            <DataValue
              key={document.noteAttachId}
              isDetail={isDetail}
              isDocument
              onClick={(event) =>
                openFilesPreview(
                  event,
                  document.noteAttachId,
                  document.fileName,
                )
              }
            >
              <FontAwesomeIcon icon={faFileLines} />
              <Tooltip title={truncate(document.fileName)} arrow>
                <span>{truncate(document.fileName, 10)}</span>
              </Tooltip>
            </DataValue>
          );
        })
      ) : (
        <EmptyField />
      );
    }
    if (Array.isArray(values)) {
      return values ? (
        values.map((value, index) => {
          const isLast = index + 1 === values.length;
          return (
            <>
              <DataValue isDetail={isDetail} hasValues>
                {value}
              </DataValue>
              {matches && !isLast && <Separator />}
            </>
          );
        })
      ) : (
        <EmptyField />
      );
    }

    if (type?.toUpperCase() === 'DATE') {
      return values ? (
        <DataValue isDetail={isDetail}>
          <FormattedDate value={values} />
        </DataValue>
      ) : (
        <EmptyField />
      );
    }
    if (type?.toUpperCase() === 'DATETIME') {
      return values ? (
        <DataValue isDetail={isDetail}>
          <FormattedDate value={values} />
          {' | '}
          <FormattedTime value={values} />
        </DataValue>
      ) : (
        <EmptyField />
      );
    }
    if (type?.toUpperCase() === 'DATETIMESEC') {
      return values ? (
        <DataValue isDetail={isDetail}>
          <FormattedDate value={values} />
          {' | '}
          <FormattedTime
            value={values}
            hour="numeric"
            minute="numeric"
            second="numeric"
          />
        </DataValue>
      ) : (
        <EmptyField />
      );
    }
    if (type?.toUpperCase() === 'TIME') {
      return values ? (
        <DataValue isDetail={isDetail}>
          <TrlTimeFormatted value={values} />
        </DataValue>
      ) : (
        <EmptyField />
      );
    }
    if (type?.toUpperCase() === 'NUMBER') {
      return values ? (
        <DataValue isDetail={isDetail}>
          <TrlNumberFormatted
            value={values}
            decimalDigits={decimalDigits}
            startDecorator={startDecorator}
            endDecorator={endDecorator}
          />
        </DataValue>
      ) : (
        <EmptyField />
      );
    }

    return <DataValue isDetail={isDetail}>{values}</DataValue>;
  };

  return (
    <DataWrapper isWallet={isWallet} isDetail={isDetail} {...rest}>
      <DataLabel isDetail={isDetail} isDocument={isDocument}>
        {label ?? <TrlNbsp />}
      </DataLabel>
      <WrapperValues isDocument={isDocument}>{getValue()}</WrapperValues>
    </DataWrapper>
  );
};
TrlLabelValue.propTypes = {
  values: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.element,
  ]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  isDetail: PropTypes.bool,
  isDocument: PropTypes.bool,
  isWallet: PropTypes.bool,
  documents: PropTypes.array,
  decimalDigits: PropTypes.number,
  startDecorator: PropTypes.node,
  endDecorator: PropTypes.node,
  type: PropTypes.oneOf([
    null,
    'date',
    'datetime',
    'datetimeSec',
    'time',
    'timeSec',
    'number',
  ]),
};
